import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Navigation.css';

const Navigation = () => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [scrollTimeout, setScrollTimeout] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      if (currentScrollY < 10) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
        setIsDropdownOpen(false);
      } else if (currentScrollY < lastScrollY) {
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);

      const timeout = setTimeout(() => {
        setIsVisible(true);
      }, 150);

      setScrollTimeout(timeout);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [lastScrollY, scrollTimeout]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Green SVG Icons
  const ForecastIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent-green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  );

  const PreMatchIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent-green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );

  const CommentIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent-green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );

  const AboutIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent-green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );

  const DailyCommentIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent-green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );

  const menuItems = [
    { path: '/days-forecast', label: 'დღის პროგნოზი', icon: <ForecastIcon /> },
    { path: '/live', label: 'LIVE', icon: <span className="live-dot"></span> },
    { path: '/winaswari-prognozi', label: 'წინასწარი პროგნოზი', icon: <PreMatchIcon /> },
  ];

  const dropdownItems = [
    { path: '/about', label: 'ჩვენს შესახებ', icon: <AboutIcon /> },
    { path: '/posts', label: 'დღის კომენტარი', icon: <DailyCommentIcon /> },
  ];

  const isDropdownActive = dropdownItems.some(item => location.pathname === item.path);

  return (
    <nav className={`navigation ${isVisible ? 'nav-visible' : 'nav-hidden'}`} ref={dropdownRef}>
      <div className="nav-container">
        <Link to="/" className="nav-logo" onClick={() => setIsDropdownOpen(false)}>
          <img src="/ntflogo.png" alt="NTF Logo" className="logo-icon" />
          <div className="logo-content">
            <span className="logo-subtitle">ფეხბურთის პროგნოზები</span>
          </div>
        </Link>

        <div className="nav-menu">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
              onClick={() => setIsDropdownOpen(false)}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-text">{item.label}</span>
            </Link>
          ))}

          {/* კომენტარი button */}
          <button
            className={`nav-link dropdown-toggle ${isDropdownOpen ? 'open' : ''} ${isDropdownActive ? 'active' : ''}`}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <span className="nav-icon"><CommentIcon /></span>
            <span className="nav-text">კომენტარი</span>
            <span className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}>▾</span>
          </button>
        </div>
      </div>

      {/* Dropdown rendered OUTSIDE nav-menu to avoid overflow clipping */}
      {isDropdownOpen && (
        <div className="dropdown-panel">
          {dropdownItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`dropdown-item ${location.pathname === item.path ? 'active' : ''}`}
              onClick={() => setIsDropdownOpen(false)}
            >
              <span className="dropdown-icon">{item.icon}</span>
              <span className="dropdown-text">{item.label}</span>
            </Link>
          ))}
        </div>
      )}
    </nav>
  );
};

export default Navigation;
